// Priority's menu tree in the sidebar. Spec §5. One fetch; only expanded
// branches render; forms open the form page; procedures and reports fold
// under one grey row per node. Task 4 adds filter, reveal, ask and resize.
import { ancestorPaths, filterPaths, findFirstPath, pathKey } from './menu-tree-logic.mjs';

const { useDeferredValue, useEffect, useMemo, useRef, useState } = React;

// Enter and Space activate a role="button". Space must preventDefault or the
// page scrolls under the user; Enter is already a no-op by default.
function keyActivate(fn) {
  return (e) => {
    if (e.key === 'Enter') fn();
    else if (e.key === ' ') { e.preventDefault(); fn(); }
  };
}

const T = {
  en: { unavailable: 'Menu tree not mirrored yet — showing the flat list.', error: 'Menu tree failed to load.',
        loading: 'Loading menu…', procs: (p, r) => [p && `${p} procedure${p === 1 ? '' : 's'}`, r && `${r} report${r === 1 ? '' : 's'}`].filter(Boolean).join(' · '),
        ask: 'ask the agent', askShort: 'ask', filter: 'Filter the tree',
        noGrants: 'No forms are available for your login yet — ask your administrator.' },
  he: { unavailable: 'עץ התפריטים עדיין לא ממופה — מוצגת הרשימה השטוחה.', error: 'טעינת עץ התפריטים נכשלה.',
        loading: 'טוען תפריט…', procs: (p, r) => [p && (p === 1 ? 'תהליך אחד' : `${p} תהליכים`), r && (r === 1 ? 'דוח אחד' : `${r} דוחות`)].filter(Boolean).join(' · '),
        ask: 'שאלו את הסוכן', askShort: 'שאל', filter: 'סינון העץ',
        noGrants: 'אין עדיין טפסים זמינים לכניסה שלך — פנה למנהל המערכת.' },
};

function title(node, lang) {
  return (lang === 'he' ? node.title_he : (node.title_en || node.title_he)) || node.ename;
}

function Row({ node, path, depth, lang, expanded, toggle, activePath, onPick, onAsk, shown }) {
  // Under a filter (`shown` non-null), a row not in `shown` renders nothing;
  // `expanded` is already the caller's effective set (filterExpanded when
  // filtering, the user's own set otherwise) -- Row itself does not choose.
  if (shown && !shown.has(path)) return null;
  const kids = node.children || [];
  const forms = kids.filter(k => k.type === 'menu' || k.type === 'form');
  const grey = kids.filter(k => k.type === 'proc' || k.type === 'report');
  const isMenu = node.type === 'menu';
  const open = expanded.has(path);
  // A matched proc/report's path is in `shown`; the synthetic `#grey` path
  // never is (filterPaths only walks real nodes). So under a filter the grey
  // group is keyed off its own children's visibility, not the pseudo-path.
  const greyEntries = grey.map(g => ({ g, p: pathKey(path, g) }));
  const greyVisible = shown ? greyEntries.filter(e => shown.has(e.p)) : greyEntries;
  const greyOpen = shown ? greyVisible.length > 0 : expanded.has(path + '#grey');
  const canOpen = forms.length > 0 || grey.length > 0;
  const clickable = node.type === 'form' && node.has_page;
  const chevLabel = open ? (lang === 'he' ? 'כווץ' : 'collapse') : (lang === 'he' ? 'הרחב' : 'expand');
  // One action, reached by pointer and by keyboard, so the two cannot drift.
  // `actionable` gates the role and the tab stop: an undumped greyed form with
  // no children does nothing when activated and must not be a dead tab stop.
  const act = () => (clickable ? onPick(node.ename, path) : canOpen && toggle(path));
  const actionable = clickable || canOpen;
  return (
    <>
      {/* Class literals stay INSIDE the attribute: scripts/check_css_classes.py
          reads strings only from className={...}, so a `cls` variable built
          above would leave `menu` unreferenced and fail the gate. */}
      <div data-path={path}
           className={['mt-row', isMenu ? 'menu' : node.type, clickable ? 'link' : '',
                       !node.has_page && !isMenu ? 'mt-grey' : '',
                       activePath === path ? 'active' : ''].filter(Boolean).join(' ')}
           style={{ paddingInlineStart: 8 + depth * 12 }}
           title={`${node.ename} · ${node.title_he}${node.title_en ? ' · ' + node.title_en : ''}`}>
        <button type="button" className={'mt-chev' + (open ? ' open' : '') + (canOpen ? '' : ' none')}
                aria-label={chevLabel} disabled={!canOpen}
                onClick={() => toggle(path)}>›</button>
        <span className="mt-label" dir="auto" onClick={act}
              role={actionable ? 'button' : undefined}
              tabIndex={actionable ? 0 : undefined}
              onKeyDown={actionable ? keyActivate(act) : undefined}>
          {title(node, lang)}
        </span>
        {isMenu && node.form_count != null && <span className="mt-count">{node.form_count}</span>}
        {!isMenu && node.type !== 'form' && <span className="mt-badge">{node.type === 'proc' ? 'P' : 'R'}</span>}
      </div>
      {open && forms.map(k => (
        <Row key={pathKey(path, k)} node={k} path={pathKey(path, k)} depth={depth + 1} lang={lang}
             expanded={expanded} toggle={toggle} activePath={activePath} onPick={onPick} onAsk={onAsk} shown={shown}/>
      ))}
      {open && greyVisible.length > 0 && (() => {
        const procsLabel = T[lang].procs(greyVisible.filter(e => e.g.type === 'proc').length,
                                          greyVisible.filter(e => e.g.type === 'report').length);
        const toggleGrey = () => toggle(path + '#grey');
        return (
          <>
            <div className="mt-row mt-group" style={{ paddingInlineStart: 8 + (depth + 1) * 12 }}
                 title={procsLabel} role="button" tabIndex={0}
                 onKeyDown={keyActivate(toggleGrey)}
                 onClick={toggleGrey}>
              <span className={'mt-chev' + (greyOpen ? ' open' : '')}>›</span>
              <span className="mt-label">{procsLabel}</span>
            </div>
            {greyOpen && greyVisible.map(({ g, p }) => (
              <div key={p} className="mt-row mt-grey" style={{ paddingInlineStart: 8 + (depth + 1) * 12 }}
                   title={`${g.ename} · ${g.title_he}`}>
                <span className="mt-label" dir="auto">{title(g, lang)}</span>
                <span className="mt-badge">{g.type === 'proc' ? 'P' : 'R'}</span>
                <button type="button" className="mt-ask" title={T[lang].ask} aria-label={T[lang].ask}
                        onClick={() => onAsk(g.ename, g.type)}>{T[lang].askShort}</button>
              </div>
            ))}
          </>
        );
      })()}
    </>
  );
}

function MenuTree({ lang, openForm, onPick, onAsk, onUnavailable, env }) {
  const [tree, setTree] = useState(null);
  const [state, setState] = useState('loading');   // loading | ok | unavailable | error
  const [expanded, setExpanded] = useState(() => new Set());
  const [activePath, setActivePath] = useState(null);
  const [query, setQuery] = useState('');
  // The occurrence the user clicked; Task 4's reveal effect defers to it.
  const pickedRef = useRef(null);
  const rootRef = useRef(null);
  const toggle = (p) => setExpanded(prev => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; });

  // env: the offline workspace's pick (spec 2026-11-02 §4.4); undefined on a connected tenant, as before.
  useEffect(() => {
    let alive = true;
    setState('loading');
    PCMAdapter.getMenuTree(env).then(d => { if (alive) { setTree(d); setState('ok'); } })
      .catch(e => {
        if (!alive) return;
        if (e.status === 404) { setState('unavailable'); onUnavailable && onUnavailable(); }
        else setState('error');
      });
    return () => { alive = false; };
  }, [env]);

  // Reveal: when a form is opened from search or a link, expand its first
  // occurrence and scroll to it. A click inside the tree already chose the
  // occurrence the user came from (pickedRef, declared in Task 3) -- keep it.
  useEffect(() => {
    if (!tree || !openForm) return;
    if (pickedRef.current === openForm) { pickedRef.current = null; return; }
    const path = findFirstPath(tree.root, openForm);
    if (!path) return;
    setExpanded(prev => new Set([...prev, ...ancestorPaths(path)]));
    setActivePath(path);
  }, [tree, openForm]);

  // [activePath] only: with `expanded` in the deps, opening any unrelated
  // branch would yank the sidebar back to the active row. The reveal effect
  // sets expanded and activePath in one batch, so the row exists by the time
  // this runs.
  useEffect(() => {
    if (!activePath || !rootRef.current) return;
    const el = rootRef.current.querySelector(`[data-path="${CSS.escape(activePath)}"]`);
    el && el.scrollIntoView({ block: 'nearest' });
  }, [activePath]);

  // shown: null means "no filter" (Row renders unconditionally, its own
  // `expanded` set decides visibility). filterExpanded auto-opens every
  // ANCESTOR of a match -- not the match itself: a matched menu whose own
  // children are filtered out would otherwise show a rotated chevron over an
  // empty branch. A match that DOES have visible descendants is still opened,
  // because it is then an ancestor of one of them.
  // The walk is over all 17,020 instances, so it runs on the deferred query:
  // typing stays responsive and the filtered result lands a frame later.
  const deferredQuery = useDeferredValue(query);
  const shown = useMemo(() => (tree ? filterPaths(tree.root, deferredQuery) : null), [tree, deferredQuery]);
  const filterExpanded = useMemo(() => (shown ? new Set([...shown].flatMap(p => ancestorPaths(p))) : null), [shown]);
  const effectiveExpanded = shown ? filterExpanded : expanded;

  const pick = (ename, path) => { pickedRef.current = ename; setActivePath(path); onPick(ename); };

  if (state === 'loading') return <div className="mt-note">{T[lang].loading}</div>;
  if (state === 'unavailable') return <div className="mt-note">{T[lang].unavailable}</div>;
  if (state === 'error') return <div className="mt-note">{T[lang].error}</div>;
  if (tree && tree.counts && tree.counts.forms === 0 && tree.counts.unfiled === 0 && tree.counts.hidden_forms > 0) {
    return <div className="mt-note" dir="auto">{T[lang].noGrants}</div>;
  }
  return (
    <>
      <input className="mt-filter input" type="search" value={query} placeholder={T[lang].filter}
             aria-label={T[lang].filter} onChange={e => setQuery(e.target.value)} dir="auto"/>
      {/* Deliberately no ARIA tree role: a tree whose items carry no treeitem
          role and no aria-expanded is worse for a screen reader than none. */}
      <div className="mt-tree" ref={rootRef}>
        {tree.root.map(n => (
          <Row key={pathKey('', n)} node={n} path={pathKey('', n)} depth={0} lang={lang}
               expanded={effectiveExpanded} toggle={toggle} activePath={activePath} onPick={pick} onAsk={onAsk}
               shown={shown}/>
        ))}
      </div>
    </>
  );
}

window.MenuTree = MenuTree;
window.MenuTreeText = T;
